// One chart in one content window (TODO §15/§4, §5, §8).
//
// **It is always live**, like a Gridd and a gantt and unlike a diagram: we render from the doc
// on every frame, so there is no at-rest representation to cache and no click-into state to
// model. What separates the editable copy from the read-only ones is the `readOnly` prop the
// whole content pipeline already threads — presentation, the print layer and the off-screen
// thumbnail capturer pass true, and it turns off the Chart/Data toggle, the tooltip, animation
// and the accessibility layer's key bindings.
//
// This file owns three things beyond drawing: the **Chart / Data toggle** (§4.1), the **CSV
// import** (§4.3), and the **refresh rules** for a URL source (§5.3). The last of those is why
// it is longer than `GanttBlock` — timers are pure UI state, they live here in a `useEffect`,
// they are never persisted, and they are torn down by this component's own unmount, which is
// what a deck switch causes (§5.3.5).

import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { AlertTriangle, RefreshCw, Table2, Upload } from "lucide-react";
import { toast } from "sonner";

import { ChartDataTable } from "@/components/deck/ChartDataTable";
import { useChartEditing } from "@/components/deck/chartEditing";
import { Button } from "@/components/ui/button";
import { useElementSize } from "@/hooks/useElementSize";
import {
  CHART_MIN_INTERVAL_MINUTES,
  CHART_STALENESS_FLOOR_MS,
  defaultEncoding,
} from "@/lib/chart";
import { loadChartCanvas } from "@/lib/chart-engine";
import { mapOntoColumns, parseCsvFile } from "@/lib/chartCsv";
import { fetchRemoteTable } from "@/lib/chartFetch";
import { resolveChartStyle } from "@/lib/stylesheet-chart";
import { sheetSwatches } from "@/lib/stylesheet-typography";
import { cn } from "@/lib/utils";
import { chartDocUpdated } from "@/store/chartSlice";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { selectEffectiveStyleSheet } from "@/store/styleDraftSlice";
import type { ChartDocument } from "@/types/chart";

// Through the engine door so the lazy boundary and the imperative export path share one chunk
// (§2.4).
const ChartCanvas = lazy(() => loadChartCanvas());

export function ChartBlock({
  docId,
  readOnly,
}: {
  docId: string;
  readOnly: boolean;
}) {
  const dispatch = useAppDispatch();
  const doc = useAppSelector((s) => s.chart[docId]);
  const { isEditor, publish, release } = useChartEditing();

  // draft ?? committed, like every other content kind: a stylesheet preview restyles the chart
  // live without a reload (§6.2.4), with no extra plumbing at all.
  const effectiveStyleSheet = useAppSelector(selectEffectiveStyleSheet);
  const style = useMemo(
    () => resolveChartStyle(effectiveStyleSheet),
    [effectiveStyleSheet],
  );
  const swatches = useMemo(
    () => sheetSwatches(effectiveStyleSheet),
    [effectiveStyleSheet],
  );

  // §8.2 The window's LOGICAL size, and nothing else. `useElementSize` reads
  // `clientWidth`/`clientHeight`, which the slide's `scale()` transform does not affect, so
  // these numbers are already in the 1600×900 canvas's units — which is what `ChartCanvas`
  // lays out in. No scale factor enters the arithmetic at any point.
  const [boxRef, box] = useElementSize<HTMLDivElement>();

  // §4.1 Which face of the window is showing. Local UI state, never on the doc: which tab an
  // author last had open is not a property of the chart, and persisting it would make it
  // travel into a nugget and an export.
  const [view, setView] = useState<"chart" | "data">("chart");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [refreshing, setRefreshing] = useState(false);

  // §15/§7.2 Give the panel up on unmount — and claim it ONLY on a pointer down (see the
  // handler on the box below), never on mount.
  //
  // **Mounting used to claim it, and that was the bug the rail exposed.** The editor mounts
  // every slide of the deck at once, so "the chart the panel edits" was whichever chart
  // initialised last, anywhere in the deck — invisible while the panel was the only consumer
  // (it showed *a* chart, and clicking one fixed it), and wrong the moment the rail started
  // asking whether there is a chart to edit right now: the answer was yes from the first
  // render of any deck containing one, on every slide, forever. A click is the selection, and
  // since we own this renderer a pointer down IS our selection event — there is no library
  // callback to wait for.
  useEffect(() => {
    if (readOnly || !isEditor) return;
    return () => release(docId);
  }, [docId, isEditor, readOnly, release]);

  const update = useCallback(
    (next: ChartDocument) => dispatch(chartDocUpdated({ docId, doc: next })),
    [dispatch, docId],
  );

  // === §5.3 Refresh =========================================================================
  //
  // One function for all three modes, so "Refresh now" and the interval tick cannot drift
  // apart. A ref rather than a state flag guards re-entry: two ticks landing while a slow
  // fetch is in flight must not both write.
  const inFlight = useRef(false);
  const refresh = useCallback(
    async (announce: boolean): Promise<void> => {
      const current = doc;
      if (!current || current.source.kind !== "url" || inFlight.current) return;
      inFlight.current = true;
      if (announce) setRefreshing(true);
      const source = current.source;
      try {
        const table = await fetchRemoteTable(source);
        // §4.5 Match onto the columns already there, so a refresh keeps the encoding, the
        // series colours and the plotted set rather than rebuilding them from the response.
        const mapped = mapOntoColumns(current.columns, table);
        const sameShape = mapped.columns.every(
          (c, i) => c.id === current.columns[i]?.id,
        );
        update({
          ...current,
          columns: mapped.columns,
          rows: mapped.rows,
          // A refresh that changed the SHAPE has to re-derive the encoding — the old one
          // points at columns that are gone. One that only changed the numbers keeps it.
          encoding: sameShape
            ? current.encoding
            : defaultEncoding(mapped.columns),
          source: {
            ...source,
            lastFetchedAt: new Date().toISOString(),
            // Clearing the error is as important as recording one: a badge that survives a
            // successful refresh is a badge nobody believes the next time.
            lastError: undefined,
          },
        });
        if (announce) {
          toast.success("Chart data updated", {
            description: mapped.notes.join(" ") || undefined,
          });
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        // §5.4.1 **The previous rows are kept.** A deck must never degrade to an empty chart
        // because a server was down — the failure is recorded beside the data, not instead of
        // it, and only the editor ever shows it (§5.4.2).
        update({
          ...current,
          source: { ...source, lastError: message },
        });
        if (announce)
          toast.error("Could not update the chart", { description: message });
      } finally {
        inFlight.current = false;
        setRefreshing(false);
      }
    },
    [doc, update],
  );

  // A stable handle for the timers below, so changing the schedule does not depend on `doc`'s
  // identity — which changes on every keystroke, and would otherwise restart the interval.
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;

  const source = doc?.source;
  const mode = source?.kind === "url" ? source.refresh.mode : null;
  const intervalMinutes =
    source?.kind === "url" ? source.refresh.intervalMinutes : undefined;
  const lastFetchedAt =
    source?.kind === "url" ? source.lastFetchedAt : undefined;

  // §5.3.2 `on-open` — once, when the deck opens, and skipped if the cached rows are younger
  // than the staleness floor so that switching between two decks does not hammer a source.
  // The effect runs once per mount, which IS "when the deck opens": `DeckRoute` keys the whole
  // editor subtree on the deckId, so a deck switch remounts every block on the canvas.
  const openedRef = useRef(false);
  useEffect(() => {
    // §5.3.4 Never on an export, print, presentation or thumbnail path. Two independent gates —
    // see the note on `isEditor`.
    if (readOnly || !isEditor || mode !== "on-open" || openedRef.current)
      return;
    openedRef.current = true;
    const age = lastFetchedAt
      ? Date.now() - Date.parse(lastFetchedAt)
      : Infinity;
    if (age < CHART_STALENESS_FLOOR_MS) return;
    void refreshRef.current(false);
  }, [isEditor, lastFetchedAt, mode, readOnly]);

  // §5.3.3 `interval` — every N minutes while the EDITOR is open, floored at the minimum.
  // Torn down by this effect's cleanup, which a deck switch, a Clear window and a layout
  // change all trigger (§5.3.5).
  useEffect(() => {
    if (readOnly || !isEditor || mode !== "interval") return;
    const minutes = Math.max(
      CHART_MIN_INTERVAL_MINUTES,
      intervalMinutes ?? CHART_MIN_INTERVAL_MINUTES,
    );
    const timer = window.setInterval(
      () => void refreshRef.current(false),
      minutes * 60_000,
    );
    return () => window.clearInterval(timer);
  }, [intervalMinutes, isEditor, mode, readOnly]);

  // §4.3 CSV import. The parser chunk is fetched on the click and not before.
  const onCsvFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // reset so re-picking the same file still fires change
    if (!file || !doc) return;
    try {
      const parsed = await parseCsvFile(file);
      const mapped = mapOntoColumns(doc.columns, parsed);
      const sameShape = mapped.columns.every(
        (c, i) => c.id === doc.columns[i]?.id,
      );
      update({
        ...doc,
        columns: mapped.columns,
        rows: mapped.rows,
        encoding: sameShape ? doc.encoding : defaultEncoding(mapped.columns),
        // §4.4 Provenance only — never the raw CSV text beside the parsed table, which would
        // double the deck's storage for a string nothing reads back.
        source: {
          kind: "csv",
          fileName: file.name,
          importedAt: new Date().toISOString(),
        },
      });
      // §4.5 What was dropped is REPORTED rather than silently applied.
      toast.success(`Imported ${mapped.rows.length} rows from ${file.name}`, {
        description: mapped.notes.join(" ") || undefined,
      });
    } catch (err) {
      toast.error("Could not read that file", {
        description: err instanceof Error ? err.message : String(err),
      });
    }
  };

  if (!doc) {
    // The reference outlived its body — a bundle imported without its charts, or a cascade
    // this window has not caught up with. Draw the window, not a crash.
    return (
      <div className="flex min-h-0 min-w-0 flex-1 items-center justify-center rounded-md border border-dashed border-slate-200 bg-slate-50/60 text-xs text-slate-400">
        This chart's data is missing.
      </div>
    );
  }

  const urlSource = doc.source.kind === "url" ? doc.source : null;
  const ready = box.width > 0 && box.height > 0;

  return (
    <div
      ref={boxRef}
      onPointerDownCapture={
        readOnly || !isEditor ? undefined : () => publish(docId)
      }
      className="relative flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-md border border-slate-200 bg-white"
    >
      {/* §4.1 The toggle, and the source chrome beside it. Hidden entirely when read-only —
          presentation, print and the thumbnail capturer must render the chart and nothing
          else, and §5.4.2 puts the error badge in the same category. */}
      {!readOnly && (
        <div className="flex shrink-0 items-center gap-1 border-b border-slate-100 px-1.5 py-1">
          <div className="flex overflow-hidden rounded border border-slate-200">
            {(["chart", "data"] as const).map((value) => (
              <button
                key={value}
                type="button"
                onClick={() => setView(value)}
                aria-pressed={view === value}
                className={cn(
                  "px-2 py-0.5 text-[11px] capitalize",
                  view === value
                    ? "bg-slate-900 text-white"
                    : "bg-white text-slate-600 hover:bg-slate-50",
                )}
              >
                {value}
              </button>
            ))}
          </div>
          {view === "data" && (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-2 text-[11px]"
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload className="mr-1 h-3 w-3" /> Import CSV
            </Button>
          )}
          {urlSource && (
            <>
              <Button
                variant="ghost"
                size="sm"
                className="h-6 px-2 text-[11px]"
                disabled={refreshing}
                onClick={() => void refresh(true)}
              >
                <RefreshCw
                  className={cn("mr-1 h-3 w-3", refreshing && "animate-spin")}
                />
                Refresh
              </Button>
              {/* §5.4.3 "Updated <relative time>", so stale data is visible rather than
                  assumed fresh — the whole reason a live source needs a label at all. */}
              <span className="truncate text-[11px] text-slate-400">
                {urlSource.lastFetchedAt
                  ? `Updated ${relativeTime(urlSource.lastFetchedAt)}`
                  : "Never updated"}
              </span>
              {/* §5.4.2 The error badge, EDITOR ONLY — it is inside the `!readOnly` block
                  above, which is what makes that true rather than a comment. */}
              {urlSource.lastError && (
                <span
                  className="flex shrink-0 items-center gap-1 rounded bg-amber-50 px-1.5 py-0.5 text-[11px] text-amber-700"
                  title={urlSource.lastError}
                >
                  <AlertTriangle className="h-3 w-3" />
                  Last refresh failed
                </span>
              )}
            </>
          )}
          {doc.source.kind === "csv" && (
            <span className="truncate text-[11px] text-slate-400">
              {doc.source.fileName}
            </span>
          )}
        </div>
      )}

      <div className="relative min-h-0 min-w-0 flex-1 p-1">
        {view === "data" && !readOnly ? (
          <ChartDataTable doc={doc} onChange={update} />
        ) : (
          // Measured through the wrapper, not the outer box: the toolbar above takes height,
          // and handing the chart the whole window's size would push it out the bottom.
          <ChartSurface
            doc={doc}
            box={box}
            readOnly={readOnly}
            style={style}
            swatches={swatches}
          />
        )}
      </div>

      {/* Hidden (display:none) so the slide's scale transform is irrelevant. */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv,text/csv,text/plain"
        className="hidden"
        onChange={(e) => void onCsvFile(e)}
      />
    </div>
  );
}

// The drawing half, measured independently of the window because the toolbar above it is not
// part of the chart's box. Split out so the measurement is a component boundary rather than a
// second `useElementSize` in a branch, which would break the rules of hooks when the Data view
// is showing.
function ChartSurface({
  doc,
  box: windowBox,
  readOnly,
  style,
  swatches,
}: {
  doc: ChartDocument;
  box: { width: number; height: number };
  readOnly: boolean;
  style: ReturnType<typeof resolveChartStyle>;
  swatches: { name: string; value: string }[];
}) {
  const [ref, box] = useElementSize<HTMLDivElement>();
  // Fall back to the window's own size on the very first frame, before the observer has
  // reported: a zero box renders nothing, and on the print path there is no second frame in
  // which to correct it.
  const width = box.width || windowBox.width;
  const height = box.height || windowBox.height;
  return (
    <div ref={ref} className="h-full w-full">
      {width > 0 && height > 0 && (
        <Suspense
          fallback={
            <div className="flex h-full w-full items-center justify-center text-xs text-slate-400">
              <Table2 className="mr-2 h-4 w-4" /> Loading the chart…
            </div>
          }
        >
          <ChartCanvas
            doc={doc}
            box={{ width, height }}
            readOnly={readOnly}
            style={style}
            swatches={swatches}
          />
        </Suspense>
      )}
    </div>
  );
}

// "3 minutes ago". `Intl.RelativeTimeFormat` rather than a hand-rolled ladder, so it is
// localized and so "1 minute" is not written "1 minutes".
const RELATIVE = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });
const UNITS: [Intl.RelativeTimeFormatUnit, number][] = [
  ["second", 60],
  ["minute", 60],
  ["hour", 24],
  ["day", 7],
  ["week", 4.35],
  ["month", 12],
  ["year", Infinity],
];

function relativeTime(iso: string): string {
  let value = (Date.parse(iso) - Date.now()) / 1000;
  for (const [unit, span] of UNITS) {
    if (Math.abs(value) < span) return RELATIVE.format(Math.round(value), unit);
    value /= span;
  }
  return iso;
}
